import { Component, DestroyRef, inject, Input, OnInit, signal } from '@angular/core';
import { CommonModalWrapperComponent } from "@shared/components/commons/common-modal-wrapper/common-modal-wrapper.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { Contact } from "@models/common/contact";
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from "@angular/forms";
import { InputComponent } from "@shared/controls/input/input.component";
import { ContactsApiService } from "@shared/services/contacts-api.service";
import { CustomFormValidators } from "@shared/validators/custom-form-validators";
import { InputPhoneNumberComponent } from "@shared/controls/input-phone-number/input-phone-number.component";
import { CheckboxComponent } from "@shared/controls/checkbox/checkbox.component";
import { SelectComponent } from "@shared/controls/select/select.component";
import { SelectOption } from "@models/ui-models/select-options";
import { ContactsService } from "@shared/services/contacts.service";
import { DividerComponent } from "@shared/components/commons/divider/divider.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { setControlDisabled } from "@shared/utils/set-control-disabled";
import { emailValidatorMsg } from "@shared/validators/custom-form-validators-messages";
import { ToastrService } from "ngx-toastr";
import { IndividualDto } from '@models/individuals/individual-dto';
import { ValidationErrorComponent } from "@shared/controls/validation-error/validation-error.component";

export interface IEditContactModalResult {
  contact: Contact;
  isPrimaryContact: boolean;
}

export interface ICommonContactFormControls {
  email: FormControl<string | null>;
  phone: FormControl<string | null>;
  carbonCopy: FormControl<string | null>;
  blindCarbonCopy: FormControl<string | null>;
  nicknameType: FormControl<string | null>;
  nickname: FormControl<string | null>;
}

export interface IEditContactFormControls extends ICommonContactFormControls {
  isPrimaryContact: FormControl<boolean | null>;
}

export type IEditContactFormGroup = FormGroup<IEditContactFormControls>

export const customNicknameOption: SelectOption = { label: 'Custom Nickname', value: 'Nickname' };

@Component({
  selector: 'app-edit-contact-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModalWrapperComponent,
    InputComponent,
    InputPhoneNumberComponent,
    CheckboxComponent,
    SelectComponent,
    DividerComponent,
    ValidationErrorComponent
  ],
  templateUrl: 'edit-contact-modal.component.html',
})
export class EditContactModalComponent implements OnInit {
  contactsService = inject(ContactsService);
  contactsApiService = inject(ContactsApiService);
  activeModal = inject(NgbActiveModal);
  toastr = inject(ToastrService);
  destroyRef = inject(DestroyRef);

  @Input() individual!: IndividualDto;
  @Input() contact: Contact | null = null;

  readonly emailValidatorMsg = emailValidatorMsg;
  readonly customNicknameOptionValue = customNicknameOption.value;
  readonly nicknameTypesOptions: SelectOption[] = [
    { label: 'Office', value: 'Office' },
    { label: 'Home', value: 'Home' },
    customNicknameOption
  ];
  readonly contactFormCustomErrors = EditContactModalComponent.contactFormCustomErrors;

  isLoading = signal<boolean>(false);
  contactModalHeader = '';
  form: IEditContactFormGroup = new FormGroup({
    email: new FormControl<string | null>(null, [CustomFormValidators.emailValidator]),
    phone: new FormControl<string | null>(null, [CustomFormValidators.phoneNumberValidator]),
    isPrimaryContact: new FormControl<boolean | null>(false),
    carbonCopy: new FormControl<string | null>(null, [CustomFormValidators.emailValidator]),
    blindCarbonCopy: new FormControl<string | null>(null, [CustomFormValidators.emailValidator]),
    nicknameType: new FormControl<string | null>(null, [Validators.required]),
    nickname: new FormControl<string | null>(null, [Validators.required]),
  });

  ngOnInit(): void {
    this.fillHeader();
    this.listenNicknameTypeChanges();
    this.setupContact();
    this.defineContactRequiredFieldsValidation();
  }

  close(): void {
    this.activeModal.dismiss();
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const contact = this.buildContact();

    this.setFormDisabled();
    this.contactsApiService.createOrUpdateContact(contact, this.form.controls.isPrimaryContact.value!)
      .subscribe({
        next: (contactId) => {
          contact.contactId = contactId;

          const result: IEditContactModalResult = {
            contact,
            isPrimaryContact: this.form.controls.isPrimaryContact.value!
          };

          this.activeModal.close(result);
        },
        error: (error) => {
          console.warn('[EditContactModalComponent] error', error);
          const editOrCrate = this.contact ? 'edit' : 'create';
          this.toastr.error(`Failed to ${ editOrCrate } contact.`, 'Error');
          this.setFormDisabled(false);
        }
      });
  }

  buildContact(): Contact {
    return new Contact({
      ...this.contact,
      ...this.individual,
      ...this.form.value as Partial<Contact>,
      nickname: this.getNickname()
    });
  }

  defineContactRequiredFieldsValidation(): void {
    EditContactModalComponent.defineContactRequiredFieldsValidation(this.form);
  }

  private setupContact(): void {
    if (this.individual.contacts.length == 0) {
      this.form.controls.isPrimaryContact.setValue(true);
      setControlDisabled(this.form.controls.isPrimaryContact);
    }

    if (!this.contact) return;

    const nicknameTypeOption = this.nicknameTypesOptions.find((option) => option.value == this.contact?.nickname) ?? customNicknameOption;
    const nickname = nicknameTypeOption == customNicknameOption
      ? this.contact?.nickname
      : null;

    setControlDisabled(this.form.controls.isPrimaryContact, this.contact.contactId == this.individual.primaryContactId);
    this.form.patchValue({
      ...this.contact,
      nicknameType: nicknameTypeOption.value as string,
      nickname,
      isPrimaryContact: this.individual.primaryContactId == this.contact?.contactId
    });
  }

  private listenNicknameTypeChanges(): void {
    this.form.controls.nicknameType.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((nicknameType) => {
        setControlDisabled(this.form.controls.nickname, nicknameType !== this.customNicknameOptionValue);
      });
  }

  private fillHeader(): void {
    this.contactModalHeader = this.contact
      ? 'Edit Contact'
      : 'Add Contact';
  }

  private setFormDisabled(isDisabled = true): void {
    this.isLoading.set(isDisabled);
    setControlDisabled(this.form, isDisabled);
    setControlDisabled(this.form.controls.isPrimaryContact, isDisabled || !this.individual.contacts.length);
    setControlDisabled(this.form.controls.nickname, isDisabled || this.form.controls.nicknameType.value !== customNicknameOption.value);
  }

  private getNickname(): string {
    if (this.form.controls.nicknameType.value !== customNicknameOption.value) {
      return this.form.controls.nicknameType.value!;
    }

    return this.form.controls.nickname.value!;
  }

  static readonly contactFormCustomErrors = { emailOrPhoneRequired: 'Please add phone number or email address to proceed.' };

  static defineContactRequiredFieldsValidation(form: FormGroup): void {
    const emailOrPhoneRequiredValidator: ValidatorFn = (control: AbstractControl) => {
      const form = control as any as IEditContactFormGroup;
      const isEmailRequiredValid = Validators.required(form.controls.email);
      const isPhoneRequiredValid = Validators.required(form.controls.phone);

      if (isEmailRequiredValid && isPhoneRequiredValid) {
        return EditContactModalComponent.contactFormCustomErrors;
      }

      return null;
    };

    form.addValidators(emailOrPhoneRequiredValidator);
  }
}
